import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Coins, Scale, BookOpen, Sunrise } from 'lucide-react';
import { usePageContent } from '../hooks/usePageContent';
import { usePageTitle } from '../hooks/usePageTitle';
import { useSiteSettings } from '../hooks/useSiteSettings';

export const About = () => {
  usePageTitle('About Us');
  const { t } = usePageContent('about');
  const settings = useSiteSettings();
  const logoUrl = settings.logo_url || '/logo.jpg';

  const pillars = [
    {
      icon: <Heart size={32} />,
      title: t('pillar_1_title', 'Health'),
      description: t(
        'pillar_1_description',
        'Nurturing the body as the sacred vessel of consciousness through balanced living, yogic practice, and pranic vitality.',
      ),
    },
    {
      icon: <Coins size={32} />,
      title: t('pillar_2_title', 'Wealth'),
      description: t(
        'pillar_2_description',
        'Cultivating dharmic prosperity — material sufficiency earned through righteous action and shared for the welfare of all.',
      ),
    },
    {
      icon: <Scale size={32} />,
      title: t('pillar_3_title', 'Harmony'),
      description: t(
        'pillar_3_description',
        'Living in equilibrium with family, community, nature, and the cosmos — the outward expression of an inward peace.',
      ),
    },
    {
      icon: <BookOpen size={32} />,
      title: t('pillar_4_title', 'Knowledge'),
      description: t(
        'pillar_4_description',
        'Pursuing sacred wisdom through study of the Vedas, Upanishads, and the living guidance of enlightened masters.',
      ),
    },
    {
      icon: <Sunrise size={32} />,
      title: t('pillar_5_title', 'Self-realization'),
      description: t(
        'pillar_5_description',
        'The ultimate flowering of the seeker — awakening to the eternal Self and abiding in the truth of Aham Brahmasmi.',
      ),
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      <section
        className="relative py-32 md:py-40 noise-texture"
        style={{
          backgroundImage: `url(${t('hero_image', 'https://images.unsplash.com/photo-1519414442781-fbd745c5b497?q=85')})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal-900/70 via-charcoal-900/50 to-transparent" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <img
              src={logoUrl}
              alt="Rishidev Foundation"
              className="w-28 h-28 md:w-32 md:h-32 rounded-full mx-auto mb-8 shadow-2xl ring-4 ring-gold-500/30"
            />
            <div className="text-saffron-400 uppercase text-sm md:text-base tracking-[0.2em] font-body font-semibold mb-6">
              {t('hero_overline', 'Our Story')}
            </div>
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-white tracking-tighter leading-none mb-6">
              {t('hero_title', 'Preserving Ancient Wisdom for')}
              <br />
              <span className="text-gold-400">{t('hero_highlight', 'Future Generations')}</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-body leading-relaxed">
              {t('hero_description', "A sacred space dedicated to the study, practice, and transmission of Sanatana Dharma's eternal teachings.")}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-gradient-to-br from-charcoal-900 to-charcoal-800 text-white noise-texture relative" data-testid="vision-section">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(230,126,34,0.4) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(212,175,55,0.3) 0%, transparent 50%)' }} />
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="text-saffron-400 uppercase text-xs md:text-sm tracking-[0.2em] font-body font-semibold mb-6">
              {t('vision_overline', 'Our Vision')}
            </div>
            <div className="inline-block px-8 py-4 mb-8 rounded-full border border-gold-500/30 bg-gold-500/5">
              <span className="font-heading italic text-gold-400 text-lg md:text-xl">
                {t('vision_sanskrit', 'लोकाः समस्ताः सुखिनो भवन्तु')}
              </span>
            </div>
            <h2 className="text-4xl md:text-6xl font-heading font-bold tracking-tight leading-tight mb-8">
              <span className="vision-shimmer">{t('vision_statement_part_1', 'Global Transformation')}</span>
              <br />
              <span className="text-white font-normal italic text-2xl md:text-3xl">{t('vision_connector', 'Through')}</span>
              <br />
              <span className="text-white">
                {t('vision_statement_part_2', 'Individual Reformation')}
              </span>
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-white/80 max-w-3xl mx-auto font-body">
              {t('vision_description', 'We envision a world where every seeker, through inner refinement and spiritual awakening, becomes a beacon of transformation — radiating peace, wisdom, and compassion into the collective consciousness of humanity.')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="text-saffron-600 uppercase text-xs md:text-sm tracking-[0.2em] font-body font-semibold mb-4">
                {t('mission_overline', 'Our Mission')}
              </div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight leading-snug text-charcoal-800 mb-6">
                {t('mission_heading', 'Illuminating Paths to Self-Realization')}
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-charcoal-600 mb-6">
                {t('mission_text_1', 'The Rishidev Foundation was established with a sacred vision: to make the profound wisdom of Sanatana Dharma accessible to seekers in the modern world. In an era of rapid change and uncertainty, we provide an anchor in timeless truth.')}
              </p>
              <p className="text-base md:text-lg leading-relaxed text-charcoal-600 mb-6">
                {t('mission_text_2', 'Our teachings draw from the Vedas, Upanishads, Bhagavad Gita, and the living tradition passed down through enlightened masters. We offer not just intellectual understanding, but practical methods for spiritual transformation.')}
              </p>
              <p className="text-base md:text-lg leading-relaxed text-charcoal-600">
                {t('mission_text_3', 'Through authentic instruction, sacred practices, and a supportive spiritual community, we guide individuals toward self-knowledge, inner peace, and union with the Divine.')}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src={t('mission_image', 'https://images.unsplash.com/photo-1746221419914-527c5d88d7f0?q=85')}
                alt="Our mission"
                className="rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.12)] w-full"
                data-testid="about-mission-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="text-saffron-600 uppercase text-xs md:text-sm tracking-[0.2em] font-body font-semibold mb-4">
              {t('pillars_overline', 'Foundation of Our Teachings')}
            </div>
            <h2 className="text-3xl md:text-4xl font-heading tracking-tight leading-snug text-charcoal-800 mb-4">
              {t('pillars_heading', 'The Five Pillars of Sanatana Dharma')}
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-charcoal-600 max-w-3xl mx-auto">
              {t(
                'pillars_description',
                'Our teachings rest upon the eternal principles that have guided spiritual seekers for thousands of years.',
              )}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {pillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                viewport={{ once: true }}
                className="bg-cream rounded-xl p-8 border border-charcoal-100"
                data-testid={`pillar-card-${index + 1}`}
              >
                <div className="w-16 h-16 bg-saffron-100 rounded-full flex items-center justify-center mb-6 text-saffron-600">
                  {pillar.icon}
                </div>
                <h3 className="text-xl md:text-2xl font-heading leading-snug text-charcoal-800 mb-4">
                  {pillar.title}
                </h3>
                <p className="text-base leading-relaxed text-charcoal-600">{pillar.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-gradient-to-br from-saffron-50 to-gold-100">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-charcoal-800 tracking-tight leading-tight mb-6">
              "{t('quote_text', 'Truth is One, Paths are Many')}"
            </h2>
            <p className="text-lg md:text-xl text-charcoal-600 font-body leading-relaxed italic">
              — {t('quote_source', 'Rig Veda')}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
